import type { CSSStyle } from '~/types'
import { Sorting } from '~/types'
import type { GanttRowBarSwitching, GanttRowData } from '~/lib/gantt'
import { toRaw } from '@vue/reactivity'
import type { ColumnBase, ColumnStyle } from '~/lib/gantt/column'
import { ColumnTypes, setColumnWidths, toggleSort } from '~/lib/gantt/column'

export type ColumnComputedStored = Pick<GanttColumnComputed, 'id' | 'sorting' | 'style' | 'type'>
export enum ColumnComputedEnum {
  equipmentInSVG = 'equipmentInSVG',
  weekPattern = 'weekPattern',
  continuousDaily = 'continuousDaily',
}

export type ComputedColumnOptions = {
  labelFn: (short: boolean) => string
  rowValueFn: (row: GanttRowData) => string
  barValueFn: (bar: GanttRowBarSwitching) => string
  style?: Partial<ColumnStyle>
  width?: number
}

export class GanttColumnComputed implements ColumnBase<ColumnTypes.computed> {
  // Common
  id: ColumnComputedEnum
  type: ColumnTypes.computed
  sorting: Sorting | null
  style: ColumnStyle

  // Specific
  labelFn: (short: boolean) => string
  rowValueFn: (row: GanttRowData) => string
  barValueFn: (bar: GanttRowBarSwitching) => string

  constructor(id: ColumnComputedEnum, options: ComputedColumnOptions) {
    this.id = id
    this.type = ColumnTypes.computed
    this.sorting = null
    this.style = {
      flexBasis: 0,
      minWidth: 32,
      flexGrow: 0,
      flexShrink: 0,
      order: 0,
    }

    this.labelFn = options.labelFn
    this.rowValueFn = options.rowValueFn
    this.barValueFn = options.barValueFn

    setColumnWidths(this)
  }

  isDateColumn() {
    return false
  }

  toggleSort() {
    toggleSort(this)
  }

  label(short = false): string {
    if (this.labelFn) {
      return this.labelFn(short)
    }
    return this.id
  }

  rowValue(row: GanttRowData): string {
    return this.rowValueFn(row)
  }

  barValue(bar: GanttRowBarSwitching): string {
    return this.barValueFn(bar)
  }

  cssClass(header = false): Record<string, boolean> {
    return {
      'bg-surface': header,
      'text-center': true,
    }
  }

  cssStyle(last: boolean): Record<keyof ColumnStyle, any> {
    return {
      minWidth: `${this.style.minWidth}px`,
      flexBasis: `${this.style.flexBasis}px`,
      flexGrow: last ? 1 : this.style.flexGrow,
      flexShrink: this.style.flexShrink,
      order: this.style.order,
    } satisfies CSSStyle
  }

  equals(other: ColumnBase<ColumnTypes>): boolean {
    return other.id == this.id
  }

  copy(): GanttColumnComputed {
    const col = new GanttColumnComputed(this.id, {
      labelFn: this.labelFn,
      rowValueFn: this.rowValueFn,
      barValueFn: this.barValueFn,
    })
    col.style = toRaw(this.style)
    col.sorting = this.sorting
    return col
  }

  toJSON(): ColumnComputedStored {
    return {
      id: this.id,
      type: this.type,
      sorting: this.sorting,
      style: this.style,
    }
  }

  static fromJSON(data: ColumnComputedStored, options: ComputedColumnOptions): GanttColumnComputed {
    const col = new GanttColumnComputed(data.id, options)
    col.sorting = data.sorting
    col.style = data.style
    return col
  }

  static create(id: ColumnComputedEnum, options: ComputedColumnOptions): GanttColumnComputed {
    const col = new GanttColumnComputed(id, options)
    setColumnWidths(col, options.width)
    return col
  }
}
